import { Global, css } from '@emotion/react';
import { fonts } from '@workday/canvas-kit-react-fonts';
import { CanvasProvider } from '@workday/canvas-kit-react/common';
import '@workday/wcp-tokens/WcpTokens.css';
import React from 'react';
import ReactDOM from 'react-dom/client';
import { initializeSentry } from 'sentryInit';
import { getDevsiteFullPath } from 'shared/utils/devsite';
import routes from 'config/developerSite/routes';
import 'modules/builder/core/theme/DevSiteTheme.css';
import { Responsive } from 'modules/common/components/Responsive';
import { Windowz } from 'modules/common/def/Windowz';
import { lightTheme } from 'modules/common/model/themes';
import { AppRouter, Routing } from 'modules/core';
import DevsiteEnv from 'modules/core/DevsiteEnv';
import { globals } from 'modules/core/GlobalStyles';
import Logout from 'modules/core/auth/Logout';
import {
  DEVSITE_AUTH_POPUP_SIGNATURE,
  FLOWBUILDER_AUTH_POPUP_SIGNATURE,
  NARWHAL_AUTH_POPUP_SIGNATURE,
  SOAP_API_EXPLORER_AUTH_POPUP_SIGNATURE,
  STUDIOBUILDER_AUTH_POPUP_SIGNATURE,
  handlePkceResponseInPopup,
} from 'modules/core/auth/oauth';
import App from 'modules/core/components/App';
import DevsiteRoute from 'modules/core/components/AppRouter/DevsiteRoute';
import { CLOUD_REDIRECT_RULES } from 'modules/core/components/AppRouter/LegacyRedirects';
import DevsiteI18nProvider from 'modules/core/components/DevsiteI18nProvider';
import DevsiteMetadata from 'modules/core/components/DevsiteMetadata';
import { ExternalLinkBlockingProvider } from 'modules/core/components/ExternalLinkBlockingProvider';
import { TrustArcScript } from 'modules/core/components/TrustArc';
import UXContextProviders from 'modules/core/components/UXContextProviders';
import { applyStoredThemeToDOM } from 'modules/core/components/themePreferenceStorage';
import {
  DEVSITE_ROOT_DEFAULT_DESCRIPTION,
  DEVSITE_ROOT_DEFAULT_PAGE_TITLE,
  DEVSITE_ROOT_DEFAULT_SOCIAL_IMAGE,
} from 'modules/core/constants/devsiteDefaultMetadata';
import { SessionProvider } from 'modules/core/context/SessionContext';
import { browserHistory } from 'modules/core/history';
import Login from 'modules/login';

// globalize react for MFEs for those who want to share instances
Windowz.React = React;
Windowz.ReactDOM = ReactDOM;

// Initialize Sentry
initializeSentry();

const rerouteToForbidden = () => {
  window.location.replace(getDevsiteFullPath('/errors/forbidden.html'));
  return null;
};

const rerouteToError = () => {
  window.location.replace(getDevsiteFullPath('/errors/error.html'));
  return null;
};

const finishPopupAuth = () => {
  handlePkceResponseInPopup(DEVSITE_AUTH_POPUP_SIGNATURE);
  return null;
};

const finishFlowbuilderPopupAuth = () => {
  handlePkceResponseInPopup(FLOWBUILDER_AUTH_POPUP_SIGNATURE);
  return null;
};

const finishSoapApiExplorerPopupAuth = () => {
  handlePkceResponseInPopup(SOAP_API_EXPLORER_AUTH_POPUP_SIGNATURE);
  return null;
};

const finishNarwhalPopupAuth = () => {
  handlePkceResponseInPopup(NARWHAL_AUTH_POPUP_SIGNATURE);
  return null;
};

const finishStudioBuilderPopupAuth = () => {
  handlePkceResponseInPopup(STUDIOBUILDER_AUTH_POPUP_SIGNATURE);
  return null;
};

// Pre-React FOUC prevention: apply the stored (or default) theme to the DOM before React hydration.
applyStoredThemeToDOM();

const container = document.getElementById('root');
if (!container) {
  throw new Error('Root element #root not found');
}
const reactRoot = ReactDOM.createRoot(container);

reactRoot.render(
  <>
    <Global styles={css(fonts, globals)} />
    <DevsiteMetadata
      title={DEVSITE_ROOT_DEFAULT_PAGE_TITLE}
      description={DEVSITE_ROOT_DEFAULT_DESCRIPTION}
      image={DEVSITE_ROOT_DEFAULT_SOCIAL_IMAGE}
      useExactTitle
    />
    {DevsiteEnv.ENABLE_ANALYTICS && <TrustArcScript />}
    <SessionProvider>
      <Responsive>
        <AppRouter history={browserHistory}>
          <CanvasProvider theme={lightTheme}>
            <DevsiteI18nProvider>
              <UXContextProviders>
                <ExternalLinkBlockingProvider>
                  <Routing redirects={CLOUD_REDIRECT_RULES}>
                    {/* Auth callback routes (OAuth popup flows) */}
                    <DevsiteRoute path={routes.auth} render={finishPopupAuth} isPublic />
                    <DevsiteRoute path={routes.orchestrateAuth} render={finishFlowbuilderPopupAuth} isPublic />
                    <DevsiteRoute path={routes.soapApiExplorerAuth} render={finishSoapApiExplorerPopupAuth} isPublic />
                    <DevsiteRoute path={routes.narwhalAuth} render={finishNarwhalPopupAuth} isPublic />
                    <DevsiteRoute path={routes.studiobuilderAuth} render={finishStudioBuilderPopupAuth} isPublic />
                    {/* Authentication flow routes */}
                    <DevsiteRoute path={routes.login} component={Login} isPublic />
                    <DevsiteRoute path={routes.logout} render={Logout} isPublic />
                    {/* Error routes */}
                    <DevsiteRoute path={routes.forbidden} render={rerouteToForbidden} isPublic />
                    <DevsiteRoute path={routes.error} render={rerouteToError} isPublic />
                    {/* Main app - all other routes handled in App.tsx unified routing tree */}
                    <DevsiteRoute path={routes.root} component={App} isPublic />
                  </Routing>
                </ExternalLinkBlockingProvider>
              </UXContextProviders>
            </DevsiteI18nProvider>
          </CanvasProvider>
        </AppRouter>
      </Responsive>
    </SessionProvider>
  </>
);
